<template>
  <div class="mhx-root">
    <router-view v-if="isLoginPage" />
    <div v-else class="mhx-app">
    <aside class="mhx-aside">
      <div class="mhx-logo">
        <div class="mhx-logo-mark">AI</div>
        <div class="mhx-logo-text">AI巡场管理</div>
      </div>
      <nav class="mhx-nav">
        <el-menu :default-active="activeMenu" :default-openeds="openedMenus" router>
          <el-menu-item index="/">
            <i class="el-icon-s-home"></i><span slot="title">工作台</span>
          </el-menu-item>

          <el-submenu v-if="canConfig" index="group-config">
            <template slot="title"><i class="el-icon-edit-outline"></i><span>巡场配置</span></template>
            <el-menu-item index="/config/plans">巡场计划</el-menu-item>
            <el-menu-item index="/config/questions">标准题库</el-menu-item>
            <el-menu-item index="/config/datasources">数据源</el-menu-item>
          </el-submenu>

          <el-submenu v-if="canSchedule" index="group-schedule">
            <template slot="title"><i class="el-icon-date"></i><span>排班与任务</span></template>
            <el-menu-item index="/schedule/list">排班管理</el-menu-item>
            <el-menu-item index="/schedule/calendar">排班总览</el-menu-item>
            <el-menu-item index="/schedule/special">特别检查</el-menu-item>
          </el-submenu>

          <el-submenu v-if="canResult" index="group-result">
            <template slot="title"><i class="el-icon-document"></i><span>巡场结果</span></template>
            <el-menu-item index="/result/daily">巡场日报</el-menu-item>
            <el-menu-item index="/result/issues">问题工单</el-menu-item>
            <el-menu-item index="/result/vision-records">图片识别记录</el-menu-item>
          </el-submenu>

          <el-submenu v-if="canSettings" index="group-settings">
            <template slot="title"><i class="el-icon-setting"></i><span>系统设置</span></template>
            <el-menu-item index="/settings/projects">项目管理</el-menu-item>
            <el-menu-item index="/settings/staff">人员管理</el-menu-item>
            <el-menu-item index="/settings/roles">角色权限</el-menu-item>
            <el-menu-item index="/settings/notifications">预警与提醒</el-menu-item>
            <el-menu-item index="/settings/ai">AI 配置</el-menu-item>
            <el-menu-item index="/settings/vision-import">导入历史问题</el-menu-item>
            <el-menu-item index="/settings/assistant-rules">助手话术</el-menu-item>
          </el-submenu>
        </el-menu>
      </nav>
    </aside>

    <div class="mhx-main-wrap">
      <header class="mhx-header">
        <span class="mhx-header-title">{{ pageTitle }}</span>
        <div class="mhx-header-meta">
          <NotificationBell />
          <el-select
            v-if="showProjectSwitcher"
            v-model="selectedProject"
            size="small"
            placeholder="当前项目"
            style="width:160px"
            @change="onProjectChange"
          >
            <el-option v-if="authContext && authContext.canViewAllProjects" label="全部项目" value="ALL" />
            <el-option v-for="p in projectOptions" :key="p.guid" :label="p.name" :value="p.guid" />
          </el-select>
          <el-tag v-if="effectiveContext" size="small" type="info">{{ userHeaderLabel }}</el-tag>
          <el-button type="text" size="small" @click="doLogout">退出登录</el-button>
          <el-link type="primary" :href="h5Url" target="_blank">打开 H5 执行端 ↗</el-link>
          <el-tag v-if="health" type="success" size="small">后端 {{ health.status }}</el-tag>
        </div>
      </header>
      <main class="mhx-content">
        <router-view :key="routerViewKey" />
      </main>
    </div>

    <button type="button" class="assistant-fab" title="AI 巡场助手" @click="openAssistant()">
      <i class="el-icon-chat-dot-round"></i>
      <span v-if="assistantBadge" class="assistant-fab-badge">{{ assistantBadge }}</span>
    </button>
    <AssistantChatDrawer ref="assistantChat" :visible.sync="assistantChatOpen" />
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { getAuthContext, getAssistantHealth, projectAuth, logout } from '@/api/admin'
import { authState } from '@/utils/projectAuth'
import AssistantChatDrawer from '@/components/AssistantChatDrawer.vue'
import NotificationBell from '@/components/NotificationBell.vue'
import { PERMS } from '@/utils/permissions'
import auth from '@/utils/auth'

const MENU_GROUPS = [
  { prefix: '/config', open: 'group-config' },
  { prefix: '/schedule', open: 'group-schedule' },
  { prefix: '/result', open: 'group-result' },
  { prefix: '/settings', open: 'group-settings' }
]

export default {
  components: { AssistantChatDrawer, NotificationBell },
  computed: {
    effectiveContext() {
      void authState.context
      return this.authContext || authState.context
    },
    userHeaderLabel() {
      const ctx = this.effectiveContext
      if (!ctx) return ''
      const user = auth.getStoredUsername()
      const role = (ctx.roleNames && ctx.roleNames[0]) || ctx.displayName || ctx.staffName
      return user ? `${role}（${user}）` : role
    },
    canConfig() {
      return this.hasPerm(PERMS.TEMPLATE_EDIT)
    },
    canSchedule() {
      return this.hasPerm(PERMS.SCHEDULE_EDIT)
    },
    canResult() {
      return this.hasPerm([PERMS.REPORT_VIEW, PERMS.ISSUE_MANAGE])
    },
    canSettings() {
      return this.hasPerm([PERMS.STAFF_MANAGE, PERMS.PERMISSION_MANAGE, PERMS.ISSUE_MANAGE])
    },
    isLoginPage() { return this.$route.path === '/login' },
    pageTitle() { return this.$route.meta.title || 'AI巡场管理' },
    activeMenu() { return this.$route.meta.activeMenu || this.$route.path },
    openedMenus() {
      if (this.$route.path === '/') return []
      const group = MENU_GROUPS.find(g => this.$route.path.startsWith(g.prefix))
      return group ? [group.open] : []
    },
    projectOptions() {
      const ctx = this.effectiveContext
      return (ctx && ctx.projects) || []
    },
    showProjectSwitcher() {
      const ctx = this.effectiveContext
      if (!ctx) return false
      return ctx.canViewAllProjects || this.projectOptions.length > 1
    },
    routerViewKey() {
      return `${this.$route.path}-${this.selectedProject || 'ALL'}-${projectAuth.getStaffGuid()}`
    },
    h5Url() {
      if (typeof window === 'undefined') return 'http://localhost:8020'
      const { hostname, protocol } = window.location
      if (hostname === 'localhost') return 'http://localhost:8020'
      return `${protocol}//${hostname}/h5/`
    }
  },
  data() {
    return {
      health: null,
      authContext: null,
      assistantChatOpen: false,
      assistantBadge: 0,
      selectedProject: projectAuth.getProjectGuid() || 'ALL'
    }
  },
  mounted() {
    axios.get('/patrol/health').then(r => { this.health = r.data }).catch(() => { this.health = { status: 'DOWN' } })
    this.refreshAuth()
    this.loadAssistantBadge()
    this.$root.$on('open-assistant-chat', this.openAssistant)
    this.$root.$on('assistant-health-changed', this.onAssistantHealthChanged)
    this.$root.$on('patrol-auth-loaded', this.applyAuthContext)
  },
  beforeDestroy() {
    this.$root.$off('open-assistant-chat', this.openAssistant)
    this.$root.$off('assistant-health-changed', this.onAssistantHealthChanged)
    this.$root.$off('patrol-auth-loaded', this.applyAuthContext)
  },
  watch: {
    isLoginPage(val) {
      if (!val) this.applyAuthContext()
    }
  },
  methods: {
    applyAuthContext() {
      if (authState.context) {
        this.authContext = authState.context
        if (this.authContext.canViewAllProjects) {
          this.selectedProject = 'ALL'
        }
      }
    },
    hasPerm(code) {
      const perms = (this.effectiveContext && this.effectiveContext.permissions) || []
      if (!code) return true
      if (Array.isArray(code)) return code.some(c => perms.includes(c))
      return perms.includes(code)
    },
    openAssistant(ctx) {
      this.assistantChatOpen = true
      this.$nextTick(() => {
        if (ctx && this.$refs.assistantChat) {
          this.$refs.assistantChat.openWithContext(ctx)
        }
      })
    },
    async loadAssistantBadge() {
      try {
        const h = await getAssistantHealth({ horizonDays: 7 })
        const n = (h.summary && h.summary.critical) || 0
        this.assistantBadge = n > 9 ? '9+' : (n || 0)
      } catch {
        this.assistantBadge = 0
      }
    },
    onAssistantHealthChanged(snapshot) {
      if (!snapshot || !snapshot.summary) return
      const n = snapshot.summary.critical || 0
      this.assistantBadge = n > 9 ? '9+' : (n || 0)
    },
    notifyAssistantContextChange() {
      this.loadAssistantBadge()
      this.$root.$emit('assistant-context-change')
      this.$root.$emit('assistant-panel-refresh')
    },
    async refreshAuth() {
      this.authContext = await projectAuth.loadContext(getAuthContext)
      if (this.authContext.currentProjectGuid) {
        this.selectedProject = this.authContext.currentProjectGuid
      } else if (this.authContext.canViewAllProjects) {
        this.selectedProject = 'ALL'
      } else if (this.projectOptions.length) {
        this.selectedProject = this.projectOptions[0].guid
        projectAuth.setProjectGuid(this.selectedProject)
      }
    },
    onProjectChange(val) {
      projectAuth.setProjectGuid(val === 'ALL' ? null : val)
      this.refreshAuth().then(() => this.notifyAssistantContextChange())
    },
    async doLogout() {
      try { await logout() } catch { /* ignore */ }
      auth.clearSession()
      projectAuth.setStaffGuid('')
      projectAuth.clearContext()
      this.authContext = null
      this.$router.replace('/login')
    }
  }
}
</script>
